// src/modules/stock/Scanner/Scanner.js
import React, { useState, useEffect, useRef } from "react";
import {
    Card, Row, Col, Tabs, Tab, Button, Alert,
    Spinner, Badge
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../../layouts/Footer";
import Header from "../../../layouts/Header";
import ScannerCamera from "./ScannerCamera";
import ScannerManuel from "./ScannerManuel";
import ResultatScan from "./ResultatScan";
import HistoriqueScans from "./HistoriqueScans";
import { formatCurrency } from "../../../utils/formatters";
import { API_BASE_URL } from "../../../constants/api";

export default function Scanner() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("camera");
    const [scanResult, setScanResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [mode, setMode] = useState('recherche');
    const [quantite, setQuantite] = useState(1);
    const [commentaire, setCommentaire] = useState('');
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [alertes, setAlertes] = useState([]);
    
    const [historiqueKey, setHistoriqueKey] = useState(Date.now());
    const historiqueRef = useRef(null);

    const getToken = () => {
        return localStorage.getItem("token");
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const token = getToken();
            const response = await fetch(
                `${API_BASE_URL}/scanner.php?action=stats`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            if (result.success) {
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const handleScan = async (code) => {
        setLoading(true);
        setError(null);
        setAlertes([]);
        
        try {
            const token = getToken();
            const response = await fetch(
                `${API_BASE_URL}/scanner.php?action=rechercher&code=${encodeURIComponent(code)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setScanResult(result.data);
                
                const alertesList = [];
                if (result.data.est_en_alerte) {
                    alertesList.push({
                        type: 'danger',
                        message: `⚠️ Stock critique : ${result.data.stock_actuel} unités (seuil: ${result.data.seuil_alerte})`
                    });
                }
                if (result.data.stock_actuel === 0) {
                    alertesList.push({
                        type: 'warning',
                        message: '📦 Produit en rupture de stock'
                    });
                }
                setAlertes(alertesList);
            } else {
                setError(result.message || "Produit non trouvé");
                setScanResult(null);
            }
        } catch (err) {
            setError(err.message || "Erreur lors du scan");
            console.error("Erreur scan:", err);
        } finally {
            setLoading(false);
        }
    };

    const refreshHistorique = () => {
        setHistoriqueKey(Date.now());
        if (historiqueRef.current && historiqueRef.current.refresh) {
            historiqueRef.current.refresh();
        }
    };

    const handleEntreeStock = async () => {
        if (!scanResult || quantite <= 0) return;
        
        setLoading(true);
        try {
            const token = getToken();
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);
            
            const response = await fetch(
                `${API_BASE_URL}/scanner.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'entree',
                        code_barre: scanResult.code_barre || scanResult.reference,
                        quantite: parseInt(quantite),
                        commentaire: commentaire,
                        id_utilisateur: user.id_utilisateur || user.id
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setSuccessMessage(`✅ ${result.data.quantite_ajoutee} ${scanResult.designation} ajouté(s) au stock`);
                setShowSuccessAlert(true);
                setTimeout(() => setShowSuccessAlert(false), 3000);
                
                await handleScan(scanResult.code_barre || scanResult.reference);
                setQuantite(1);
                setCommentaire('');
                await fetchStats();
                
                refreshHistorique();
                
            } else {
                setError(result.message || "Erreur lors de l'entrée");
            }
        } catch (err) {
            setError(err.message || "Erreur lors de l'entrée");
            console.error("Erreur entrée stock:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSortieStock = async () => {
        if (!scanResult || quantite <= 0) return;
        
        setLoading(true);
        try {
            const token = getToken();
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);
            
            const response = await fetch(
                `${API_BASE_URL}/scanner.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'sortie',
                        code_barre: scanResult.code_barre || scanResult.reference,
                        quantite: parseInt(quantite),
                        commentaire: commentaire,
                        id_utilisateur: user.id_utilisateur || user.id
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setSuccessMessage(`✅ ${result.data.quantite_retiree} ${scanResult.designation} retiré(s) du stock`);
                setShowSuccessAlert(true);
                setTimeout(() => setShowSuccessAlert(false), 3000);
                
                await handleScan(scanResult.code_barre || scanResult.reference);
                setQuantite(1);
                setCommentaire('');
                await fetchStats();
                
                refreshHistorique();
                
                if (result.data.alerte) {
                    setAlertes([{ type: 'warning', message: result.data.alerte }]);
                }
            
            } else {
                setError(result.message || "Erreur lors de la sortie");
            }
        } catch (err) {
            setError(err.message || "Erreur lors de la sortie");
            console.error("Erreur sortie stock:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleVoirProduit = () => {
        if (scanResult) {
            navigate(`/stock/produits/${scanResult.id_produit}`);
        }
    };

    const handleVoirHistorique = () => {
        setActiveTab("historique");
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setQuantite(1);
        setCommentaire('');
        setError(null);
    };

    const getStockStatusColor = (stock, seuil) => {
        if (stock <= 0) return '#ef4444';
        if (stock <= seuil) return '#f59e0b';
        return '#10b981';
    };

    const getStockProgress = (stock, max = 100) => {
        return Math.min((stock / max) * 100, 100);
    };

    if (loading && !scanResult) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Initialisation du scanner...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Alertes modernes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-qr-code-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Scanner codes-barres</h4>
                                    <p className="text-muted small mb-0">
                                        Scannez un code-barres pour rechercher, ajouter ou retirer du stock
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant={mode === 'recherche' ? 'primary' : 'outline-primary'}
                                onClick={() => handleModeChange('recherche')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={mode === 'recherche' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-search-line"></i>
                                Recherche
                            </Button>
                            <Button 
                                variant={mode === 'entree' ? 'success' : 'outline-success'}
                                onClick={() => handleModeChange('entree')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-add-circle-line"></i>
                                Entrée
                            </Button>
                            <Button 
                                variant={mode === 'sortie' ? 'danger' : 'outline-danger'}
                                onClick={() => handleModeChange('sortie')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-subtract-line"></i>
                                Sortie
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={6}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-qr-code-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Scans aujourd'hui</span>
                                        <h3 className="kpi-value">{stats.aujourdhui || 0}</h3>
                                        <span className="kpi-unit">scans effectués</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={6}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-stack-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Top scan</span>
                                        <h6 className="kpi-value" style={{ fontSize: '0.9rem' }}>
                                            {stats.top_produits?.[0]?.designation || '-'}
                                        </h6>
                                        <span className="kpi-unit">produit le plus scanné</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col lg={activeTab === 'historique' ? 12 : 7}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <Tabs
                                        activeKey={activeTab}
                                        onSelect={(k) => setActiveTab(k)}
                                        className="scanner-tabs"
                                    >
                                        <Tab 
                                            eventKey="camera" 
                                            title={
                                                <span>
                                                    <i className="ri-camera-line me-2"></i>
                                                    Caméra
                                                </span>
                                            }
                                        />
                                        <Tab 
                                            eventKey="manuel" 
                                            title={
                                                <span>
                                                    <i className="ri-keyboard-line me-2"></i>
                                                    Saisie manuelle
                                                </span>
                                            }
                                        />
                                        <Tab 
                                            eventKey="historique" 
                                            title={
                                                <span>
                                                    <i className="ri-history-line me-2"></i>
                                                    Historique
                                                </span>
                                            }
                                        />
                                    </Tabs>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {activeTab === 'camera' && (
                                        <ScannerCamera 
                                            onScan={handleScan}
                                            loading={loading}
                                            mode={mode}
                                        />
                                    )}
                                    {activeTab === 'manuel' && (
                                        <ScannerManuel 
                                            onScan={handleScan}
                                            loading={loading}
                                            mode={mode}
                                        />
                                    )}
                                    {activeTab === 'historique' && (
                                        <HistoriqueScans 
                                            key={historiqueKey}
                                            ref={historiqueRef}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {activeTab !== 'historique' && (
                            <Col lg={5}>
                                <ResultatScan
                                    scanResult={scanResult}
                                    loading={loading}
                                    error={error}
                                    alertes={alertes}
                                    mode={mode}
                                    quantite={quantite}
                                    setQuantite={setQuantite}
                                    commentaire={commentaire}
                                    setCommentaire={setCommentaire}
                                    onEntree={handleEntreeStock}
                                    onSortie={handleSortieStock}
                                    onVoirProduit={handleVoirProduit}
                                    getStockStatusColor={getStockStatusColor}
                                    getStockProgress={getStockProgress}
                                    formatCurrency={formatCurrency}
                                />
                            </Col>
                        )}
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Scanner Tabs */
                .scanner-tabs {
                    border-bottom: 2px solid #e2e8f0;
                }
                .scanner-tabs .nav-link {
                    border: none;
                    color: #64748b;
                    font-weight: 500;
                    padding: 0.75rem 1.25rem;
                    transition: all 0.2s ease;
                }
                .scanner-tabs .nav-link:hover {
                    color: #21ACCD;
                }
                .scanner-tabs .nav-link.active {
                    color: #21ACCD;
                    background: transparent;
                    border-bottom: 2px solid #21ACCD;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}